import type { CSSProperties, ReactNode } from 'react';
import { ic } from '@/components/Icons';
import { useIsMobile } from '@/hooks/useIsMobile';

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  width?: number;
  /** Floating viewers use the blue title bar (#2E7EEE). */
  headerStyle?: CSSProperties;
}

/** Centred dialog on desktop, bottom sheet on mobile. */
export function Modal({ open, onClose, title, children, footer, width = 520, headerStyle }: ModalProps) {
  const isMobile = useIsMobile();
  if (!open) return null;

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(15,30,61,0.45)', backdropFilter: 'blur(2px)',
        display: 'flex', alignItems: isMobile ? 'flex-end' : 'center', justifyContent: 'center', zIndex: 100,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="card"
        role="dialog"
        aria-modal="true"
        style={
          isMobile
            ? {
                width: '100%', maxWidth: '100%', maxHeight: '92dvh', overflow: 'auto',
                borderRadius: '14px 14px 0 0', boxShadow: 'var(--shadow-3)', border: 'none',
                display: 'flex', flexDirection: 'column',
              }
            : {
                width, maxWidth: 'calc(100vw - 32px)', maxHeight: 'calc(100vh - 64px)', overflow: 'auto',
                boxShadow: 'var(--shadow-3)', border: 'none',
              }
        }
      >
        <div
          style={{
            padding: '14px 18px', borderBottom: '1px solid var(--line)',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            position: isMobile ? 'sticky' : 'static', top: 0, background: 'var(--surface)', zIndex: 2,
            ...(headerStyle || {}),
          }}
        >
          <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>{title}</h3>
          <button className="btn icon ghost" onClick={onClose} aria-label="close" style={{ color: 'inherit' }}>
            {ic.close}
          </button>
        </div>
        <div style={{ padding: isMobile ? 14 : 18 }}>{children}</div>
        {footer ? (
          <div
            style={{
              padding: '12px 18px', borderTop: '1px solid var(--line)',
              display: 'flex', justifyContent: 'flex-end', gap: 8, background: 'var(--surface-2)',
              position: isMobile ? 'sticky' : 'static', bottom: 0, zIndex: 2,
            }}
          >
            {footer}
          </div>
        ) : null}
      </div>
    </div>
  );
}

/** Title bar used by every floating document viewer / editor dialog. */
export const BLUE_HEADER: CSSProperties = {
  background: 'var(--modal-head)',
  color: '#fff',
  borderBottomColor: 'var(--modal-head)',
};
